// RefcodeMonthPie.jsx
import React, { useMemo, useState } from "react";
import { PieChart } from "react-minimal-pie-chart";

const DATE_RE = /(\d{2})(\d{2})(\d{4})/;

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const COLORS = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2",
  "#59a14f", "#edc948", "#b07aa1", "#ff9da7",
  "#9c755f", "#bab0ab", "#2f4b7c", "#a05195",
];

function getRefcodeFromItem(it) {
  if (!it || typeof it !== "object") return null;
  return (
    it.refcode ??
    it.refCode ??
    it.ref_code ??
    it.Refcode ??
    it.RefCode ??
    it.REFCODE ??
    null
  );
}

function getMonthIndexFromRefcode(refcode) {
  if (typeof refcode !== "string") return null;

  const match = refcode.match(DATE_RE);
  if (!match) return null;

  const month = Number(match[2]); // MM
  if (!Number.isFinite(month) || month < 1 || month > 12) return null;

  return month - 1; // 0..11
}

function normalizeItems(items) {
  if (Array.isArray(items)) return items;
  if (items && typeof items === "object") return Object.values(items);
  return [];
}

function formatPct(p) {
  // p is already in percent, like 7.234
  return `${Math.round(p)}%`;
}

export default function RefcodeMonthPie({ items, noDataImage }) {
  const [selected, setSelected] = useState(undefined);
  const lineWidth = 60;

  const normalizedItems = useMemo(() => normalizeItems(items), [items]);

  // Build counts and chart data
  const { pieData, total } = useMemo(() => {
    const counts = new Array(12).fill(0);

    for (const it of normalizedItems) {
      const refcode = getRefcodeFromItem(it);
      const idx = getMonthIndexFromRefcode(refcode);
      if (idx !== null) counts[idx] += 1;
    }

    const totalCount = counts.reduce((a, b) => a + b, 0);

    // Only show months that exist (change filter to keep zeros if you want)
    const data = counts
      .map((value, i) => ({
        title: MONTH_LABELS[i],
        value,
        color: COLORS[i % COLORS.length],
        monthIndex: i,
      }))
      .filter((d) => d.value > 0);

    return { pieData: data, total: totalCount };
  }, [normalizedItems]);

  const hasAnyData = total > 0;

  return (
    <>
      <label className="pie_chart_label">Groups by Month</label>

      {!hasAnyData ? (
        <img src={noDataImage} alt="" />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {/* Chart */}
          <PieChart
            style={{
              fontFamily:
                '"Nunito Sans", -apple-system, Helvetica, Arial, sans-serif',
              fontSize: "8px",
              height: 400,
              width: 400,
            }}
            animate
            data={pieData}
            radius={44}
            lineWidth={lineWidth}
            segmentsStyle={{ transition: "stroke .3s", cursor: "pointer" }}
            segmentsShift={(index) => (index === selected ? 6 : 1)}
            label={({ dataEntry }) => formatPct(dataEntry.percentage)}
            labelPosition={100 - lineWidth / 2}
            labelStyle={{
              fill: "#fff",
              opacity: 0.8,
              pointerEvents: "none",
            }}
            onClick={(_, index) => {
              setSelected(index === selected ? undefined : index);
            }}
          />

          {/* Legend (right side) */}
          <div style={{ minWidth: 160 }}>
            <div style={{ fontSize: 12, marginBottom: 8, opacity: 0.8 }}>
              Total: <b>{total}</b>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pieData.map((d, i) => {
                const pct = total ? (d.value / total) * 100 : 0;
                const isActive = selected === i;

                return (
                  <div
                    key={d.title}
                    onClick={() => setSelected(isActive ? undefined : i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      cursor: "pointer",
                      padding: "4px 6px",
                      borderRadius: 6,
                      background: isActive ? "rgba(0,0,0,0.06)" : "transparent",
                    }}
                    title="Click to highlight"
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: d.color,
                          display: "inline-block",
                        }}
                      />
                      <span style={{ fontSize: 12 }}>{d.title}</span>
                    </div>

                    <div style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap" }}>
                      <b>{d.value}</b> <span style={{ opacity: 0.7 }}>({formatPct(pct)})</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
